import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import ThinkingPulse from "@/components/ThinkingPulse";
import { Cloud, Lock, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Settings2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { llmAnalyze, probeLocalOllama, LLMMode } from "@/services/llmRouter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const LS_KEY = "grafton_cfo_prefs_v1";

interface CfoContext {
  fiscal_year: number;
  business: { company_name?: string; sector?: string; main_offer?: string };
  treasury: {
    total_revenue_eur: number;
    total_charges_eur: number;
    total_salaries_eur: number;
    total_marketing_eur: number;
    net_margin_eur: number;
    margin_pct: number;
    monthly_revenue: { month: string; value: number }[];
  };
  crm: { total_contacts: number; qualified: number; clients: number };
  vc_pipeline: { sent: number; replied: number; signed: number; potential_eur: number; signed_eur: number };
}

const fmtEur = (n: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function CFOVirtualPanel() {
  const [mode, setMode] = useState<LLMMode>("cloud");
  const [localUrl, setLocalUrl] = useState("http://localhost:11434");
  const [localModel, setLocalModel] = useState("gemma2:9b");
  const [showSettings, setShowSettings] = useState(false);
  const [localProbe, setLocalProbe] = useState<{ reachable: boolean; models?: string[]; error?: string } | null>(null);
  const [context, setContext] = useState<CfoContext | null>(null);
  const [question, setQuestion] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<{ provider: string; text: string } | null>(null);
  const [loadingCtx, setLoadingCtx] = useState(true);

  // Load prefs
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
      if (saved.mode === "cloud" || saved.mode === "local") setMode(saved.mode);
      if (saved.localUrl) setLocalUrl(saved.localUrl);
      if (saved.localModel) setLocalModel(saved.localModel);
    } catch { /* tolerate */ }
  }, []);

  // Persist prefs
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ mode, localUrl, localModel }));
  }, [mode, localUrl, localModel]);

  // Load financial context
  const loadContext = async () => {
    setLoadingCtx(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cfo/context?fiscal_year=2026`, { credentials: "include" });
      if (r.ok) setContext(await r.json());
    } catch { /* tolerate */ }
    setLoadingCtx(false);
  };
  useEffect(() => { loadContext(); }, []);

  // Probe Ollama when switching to local or changing URL
  useEffect(() => {
    if (mode !== "local") return;
    (async () => setLocalProbe(await probeLocalOllama(localUrl)))();
  }, [mode, localUrl]);

  const runAnalysis = async () => {
    if (!context) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const res = await llmAnalyze(mode, {
        context,
        userQuestion: question.trim() || undefined,
        localUrl,
        localModel,
      });
      if (res.ok) {
        setAnalysis({ provider: res.provider, text: res.analysis });
        toast.success(`Analyse générée (${res.provider})`);
      } else {
        toast.error(res.error || "Analyse échouée");
      }
    } catch {
      toast.error("Erreur inattendue");
    }
    setAnalyzing(false);
  };

  const t = context?.treasury;
  const canRun = !!context && !analyzing && (mode === "cloud" || (mode === "local" && localProbe?.reachable));

  return (
    <Card className="bg-card border-border" data-testid="cfo-virtual-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> CFO Virtuel
            </CardTitle>
            <CardDescription>
              Analyse de tes revenus réels + plan d'action. {mode === "local" ? "🔒 Mode local — zéro donnée ne quitte ta machine." : "☁️ Mode cloud — Grafton IA (moteur DeepSeek, serveur externe)."}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant={mode === "cloud" ? "default" : "outline"}
              className="h-8 gap-1.5"
              onClick={() => setMode("cloud")}
              data-testid="cfo-mode-cloud"
            >
              <Cloud className="h-3.5 w-3.5" /> Cloud
            </Button>
            <Button
              size="sm"
              variant={mode === "local" ? "default" : "outline"}
              className="h-8 gap-1.5"
              onClick={() => setMode("local")}
              data-testid="cfo-mode-local"
            >
              <Lock className="h-3.5 w-3.5" /> Local
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setShowSettings((s) => !s)} data-testid="cfo-settings-toggle">
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={loadContext} title="Rafraîchir les données">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Settings (local mode) */}
        {showSettings && mode === "local" && (
          <div className="p-3 rounded-lg border border-border/40 bg-muted/20 space-y-2" data-testid="cfo-local-settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">URL Ollama</label>
                <Input value={localUrl} onChange={(e) => setLocalUrl(e.target.value)} className="h-8 text-xs" data-testid="cfo-local-url" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Modèle</label>
                <Input value={localModel} onChange={(e) => setLocalModel(e.target.value)} className="h-8 text-xs" data-testid="cfo-local-model" />
              </div>
            </div>
            {localProbe && (
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                {localProbe.reachable ? (
                  <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/30"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Ollama joignable</Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] text-rose-400 border-rose-500/30"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Ollama injoignable</Badge>
                )}
                {localProbe.models && localProbe.models.length > 0 && (
                  <span className="text-muted-foreground">Modèles : {localProbe.models.slice(0, 5).join(", ")}</span>
                )}
                {!localProbe.reachable && (
                  <span className="text-muted-foreground/80">
                    → Lance <code className="bg-muted px-1 rounded">OLLAMA_ORIGINS=* ollama serve</code> puis <code className="bg-muted px-1 rounded">ollama pull {localModel}</code>
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Financial snapshot */}
        {loadingCtx ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
        ) : context && t ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cfo-snapshot">
            <div className="border border-border rounded-lg p-2 bg-background/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">CA {context.fiscal_year}</p>
              <p className="text-lg font-bold leading-none mt-0.5">{fmtEur(t.total_revenue_eur)}</p>
            </div>
            <div className="border border-border rounded-lg p-2 bg-background/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Charges</p>
              <p className="text-lg font-bold leading-none mt-0.5 text-rose-400">{fmtEur(t.total_charges_eur)}</p>
            </div>
            <div className="border border-border rounded-lg p-2 bg-background/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Marge nette</p>
              <p className={`text-lg font-bold leading-none mt-0.5 ${t.net_margin_eur >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtEur(t.net_margin_eur)}</p>
              <p className="text-[10px] text-muted-foreground">{t.margin_pct}%</p>
            </div>
            <div className="border border-border rounded-lg p-2 bg-background/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Clients CRM</p>
              <p className="text-lg font-bold leading-none mt-0.5">{context.crm.clients}</p>
              <p className="text-[10px] text-muted-foreground">{context.crm.qualified} qualifiés</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Pas de données — remplis ta table Trésorerie.</p>
        )}

        {/* Question + analyze button */}
        <Textarea
          placeholder="Question libre (optionnel). Ex: 'Comment atteindre 100k€/mois d'ici septembre ?'"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          className="text-xs"
          data-testid="cfo-question"
        />
        <Button onClick={runAnalysis} disabled={!canRun} className="gap-1.5 w-full" data-testid="cfo-analyze-btn">
          {analyzing ? <ThinkingPulse size="sm" tone="light" label="" /> : <Sparkles className="h-4 w-4" />}
          {analyzing
            ? (mode === "local" ? "Grafton IA analyse en local…" : "Grafton IA analyse…")
            : `Lancer l'analyse ${mode === "local" ? "en local (100% privé)" : "avec Grafton IA"}`}
        </Button>

        {/* Analysis output */}
        {analysis && (
          <div className="p-3 rounded-lg border border-border bg-background space-y-2" data-testid="cfo-analysis-output">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[9px] text-primary">{analysis.provider}</Badge>
              <span className="text-[10px] text-muted-foreground">
                {mode === "local" ? "🔒 Aucune donnée transmise au cloud" : "☁️ Via Grafton IA"}
              </span>
            </div>
            <pre className="whitespace-pre-wrap text-xs font-sans leading-relaxed">{analysis.text}</pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
